import {
  cloneElement,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import { usePreferHoverChromeTooltips } from '../hooks/usePreferHoverChromeTooltips'

const GAP_PX = 6
const PAD_PX = 8

const tooltipPanelClass = (angular: boolean) =>
  [
    'pointer-events-none fixed z-[380] w-max max-w-[min(20rem,calc(100vw-1rem))] border border-line bg-panel px-2.5 py-1.5 text-left text-[11px] font-medium text-ink shadow-card',
    'break-words whitespace-pre-wrap transition-[opacity,visibility] duration-150',
    angular ? 'rounded-none' : 'rounded-md',
  ].join(' ')

type Props = {
  /** 悬停说明（可换行、较长正文） */
  content: ReactNode
  children: ReactElement
  angular: boolean
  /** 提示相对触发器的位置 */
  side?: 'top' | 'bottom'
  /** 气泡在触发器水平对齐方式，避免边缘按钮溢出 */
  align?: 'start' | 'center' | 'end'
  /** 为触发器根节点追加 className，默认加入 group 与 relative */
  className?: string
}

function clamp(n: number, min: number, max: number) {
  if (max < min) return min
  return Math.min(Math.max(n, min), max)
}

/**
 * 与顶栏主题切换等一致：实色 `bg-panel`、细边框、弱阴影，不依赖浏览器原生 `title`。
 * 提示层通过 Portal 挂到 `document.body` + `position: fixed`，并按视口钳位，避免被 `overflow` / `transform` 祖先裁剪或在边缘溢出。
 */
export function ChromeTooltip({
  content,
  children,
  angular,
  side = 'top',
  align = 'center',
  className = '',
}: Props) {
  const hoverTooltipsOk = usePreferHoverChromeTooltips()
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null)
  const triggerRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLSpanElement>(null)

  const hasPositionClass = /\b(absolute|fixed|relative|sticky)\b/.test(className)
  const prevClass =
    isValidElement(children) && typeof children.props === 'object' && children.props !== null && 'className' in children.props
      ? String((children.props as { className?: string }).className ?? '')
      : ''

  const updatePosition = useCallback(() => {
    if (!open) return
    const trig = triggerRef.current
    const tip = tooltipRef.current
    if (!trig || !tip) return

    const tr = trig.getBoundingClientRect()
    const tt = tip.getBoundingClientRect()
    const tw = tt.width
    const th = tt.height
    const vw = window.innerWidth
    const vh = window.innerHeight

    let top: number
    let left: number

    const vMaxTop = Math.max(PAD_PX, vh - PAD_PX - th)
    const vMaxLeft = Math.max(PAD_PX, vw - PAD_PX - tw)

    if (side === 'top') {
      top = tr.top - GAP_PX - th
      if (top < PAD_PX) {
        const belowTop = tr.bottom + GAP_PX
        if (belowTop + th <= vh - PAD_PX) {
          top = belowTop
        }
      }
      top = clamp(top, PAD_PX, vMaxTop)
    } else {
      top = tr.bottom + GAP_PX
      if (top + th > vh - PAD_PX) {
        const aboveTop = tr.top - GAP_PX - th
        if (aboveTop >= PAD_PX) {
          top = aboveTop
        }
      }
      top = clamp(top, PAD_PX, vMaxTop)
    }

    if (align === 'end') {
      left = tr.right - tw
    } else if (align === 'start') {
      left = tr.left
    } else {
      left = tr.left + tr.width / 2 - tw / 2
    }

    left = clamp(left, PAD_PX, vMaxLeft)

    setCoords({ top, left })
  }, [align, open, side])

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null)
      return
    }
    updatePosition()
  }, [content, open, updatePosition])

  useLayoutEffect(() => {
    if (!open || typeof ResizeObserver === 'undefined') return
    const trig = triggerRef.current
    const tip = tooltipRef.current
    if (!trig || !tip) return
    const ro = new ResizeObserver(() => updatePosition())
    ro.observe(trig)
    ro.observe(tip)
    return () => ro.disconnect()
  }, [open, updatePosition])

  useEffect(() => {
    if (!open) return
    const onWin = () => updatePosition()
    window.addEventListener('resize', onWin)
    window.addEventListener('scroll', onWin, true)
    return () => {
      window.removeEventListener('resize', onWin)
      window.removeEventListener('scroll', onWin, true)
    }
  }, [open, updatePosition])

  if (!hoverTooltipsOk) {
    return <>{children}</>
  }

  const tooltipShown = open && coords != null
  const portal =
    open && typeof document !== 'undefined'
      ? createPortal(
          <span
            ref={tooltipRef}
            role="tooltip"
            style={{ top: coords?.top ?? 0, left: coords?.left ?? 0 }}
            className={[
              tooltipPanelClass(angular),
              tooltipShown ? 'visible opacity-100' : 'invisible opacity-0',
            ].join(' ')}
          >
            {content}
          </span>,
          document.body,
        )
      : null

  const tailClass = className.trim()
  const usesExplicitFlex =
    /\bflex\b/.test(tailClass) || /\binline-flex\b/.test(tailClass)
  const defaultTriggerShell =
    hasPositionClass ? '' : usesExplicitFlex ? 'relative max-w-full' : 'relative inline-flex max-w-full'

  return (
    <>
      <div
        ref={triggerRef}
        className={[defaultTriggerShell, tailClass].filter(Boolean).join(' ')}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        {cloneElement(children, {
          ...(prevClass.trim() ? { className: prevClass } : {}),
        })}
      </div>
      {portal}
    </>
  )
}
